import { useCallback, useEffect, useRef, useState } from 'react';
import { isCallable, type CoinView } from './model';

export const LIVE_NOTICE_MS = 5000;
export interface LiveArrival {
  id: string;
  shownAt: number;
}

/** Only fresh, actionable chain arrivals; game/simulation coins never trigger it. */
export function useLiveArrivalNotice({
  coins,
  now,
  enabled,
  connected,
}: {
  coins: CoinView[];
  now: number;
  enabled: boolean;
  connected: boolean;
}) {
  const seen = useRef(new Map<string, number>());
  const initialized = useRef(false);
  const [arrival, setArrival] = useState<LiveArrival | null>(null);
  const dismiss = useCallback(() => setArrival(null), []);
  const available = coins.filter(
    (coin) =>
      !coin.simulated &&
      !coin.id.startsWith('game-') &&
      !coin.stale &&
      isCallable(coin) &&
      now >= coin.start &&
      now < coin.closesAt,
  );

  useEffect(() => {
    if (!connected) {
      initialized.current = false;
      setArrival(null);
      return;
    }
    // Expired rounds cannot become eligible again; keep session memory bounded.
    for (const [id, closesAt] of seen.current)
      if (closesAt <= now) seen.current.delete(id);
    const fresh = available.filter((coin) => !seen.current.has(coin.id));
    for (const coin of available) seen.current.set(coin.id, coin.closesAt);
    if (!initialized.current) {
      // Initial/reconnected snapshots are a baseline, not a burst of new tokens.
      initialized.current = true;
      return;
    }
    if (!enabled) {
      setArrival(null);
      return;
    }
    if (fresh.length) {
      const latest = fresh.reduce((a, b) => (a.start >= b.start ? a : b));
      setArrival({ id: latest.id, shownAt: Date.now() });
    } else {
      setArrival((current) =>
        current && !available.some((coin) => coin.id === current.id)
          ? null
          : current,
      );
    }
  }, [available, now, enabled, connected]);

  useEffect(() => {
    if (!arrival) return;
    const timeout = window.setTimeout(
      () => setArrival((current) => (current === arrival ? null : current)),
      Math.max(0, arrival.shownAt + LIVE_NOTICE_MS - Date.now()),
    );
    return () => window.clearTimeout(timeout);
  }, [arrival]);

  return {
    arrival:
      enabled && connected && available.some((coin) => coin.id === arrival?.id)
        ? arrival
        : null,
    dismiss,
  };
}
